import {
    centsToFormattedDollars,
    formatOptional,
    SHORT_READABLE_DATE_FORMAT,
    transformEnumToDisplay,
} from '@fruitful/fruitils';
import {
    ReadableApexAccountRegistrationType,
    type SupportedApexAccountRegistrationType,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { type Href, Link } from 'expo-router';
import { useAtomValue } from 'jotai';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Tag } from 'primereact/tag';
import React, { useMemo, useState } from 'react';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H6Emphasized,
    IconButton,
    Spinner,
    SpinnerSize,
    TrashIcon,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { getPersonDisplayName } from '../../common/utils/formatting';
import { adminUserAtom } from '../../entities/entityAtoms';
import { CopyableTextField } from '../../member/components/renderers/CopyableTextField';
import {
    ICT_WITHDRAWAL_REQUESTS_QUERY_KEY,
    useDeleteICTWithdrawalRequest,
    useGetICTWithdrawalRequests,
    usePatchICTWithdrawalRequest,
} from '../hooks/useIctData';
import type {
    AdminICTWithdrawalRequestRow,
    ICTWithdrawalRequestStatus,
} from '../types/ImOpsTypes';

interface WithdrawalTableProps {
    title: string;
    rows: AdminICTWithdrawalRequestRow[];
    showActionColumn: boolean;
    showDeleteColumn?: boolean;
    adminUserId?: string | null;
    advancingId?: string | null;
    deletingId?: string | null;
    onAdvance?: (
        row: AdminICTWithdrawalRequestRow,
        nextStatus: ICTWithdrawalRequestStatus,
    ) => Promise<void>;
    onDelete?: (row: AdminICTWithdrawalRequestRow) => Promise<void>;
}

/**
 * Ops workflow:
 *   - Requested
 *   - Neczar sells
 *   - Cash settles
 *   - Neczar submits
 *   - Colin approves
 *   - Done
 */
const ICT_WITHDRAWAL_STATUS_PIPELINE: ICTWithdrawalRequestStatus[] = [
    'REQUESTED',
    'SOLD',
    'SETTLED',
    'TRANSFER_SUBMITTED',
    'TRANSFER_APPROVED',
    'DONE',
];

const getNextPipelineStatus = (
    current: ICTWithdrawalRequestStatus,
): ICTWithdrawalRequestStatus | null => {
    if (current === 'ERROR' || current === 'DONE') {
        return null;
    }
    const i = ICT_WITHDRAWAL_STATUS_PIPELINE.indexOf(current);
    if (i === -1 || i >= ICT_WITHDRAWAL_STATUS_PIPELINE.length - 1) {
        return null;
    }
    return ICT_WITHDRAWAL_STATUS_PIPELINE[i + 1] ?? null;
};

const statusTagSeverity = (
    status: ICTWithdrawalRequestStatus,
): 'success' | 'info' | 'warning' | 'danger' => {
    switch (status) {
        case 'DONE':
            return 'success';
        case 'ERROR':
            return 'danger';
        default:
            return 'warning';
    }
};

const prettifySyncteraAccountType = (raw: string): string => {
    const parts = raw.split(/[._]/).filter((p) => p.length > 0);
    const acronyms = new Set(['IRA', 'HSA', 'CD', 'ROTH']);
    return parts
        .map((p) => {
            const u = p.toUpperCase();
            if (acronyms.has(u)) {
                return u === 'ROTH' ? 'Roth' : u;
            }
            return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
        })
        .join(' ');
};

const formatToBankLabel = (row: AdminICTWithdrawalRequestRow): string => {
    const b = row.toBankAccount;
    if (b != null) {
        return prettifySyncteraAccountType(b.accountType);
    }
    return `Bank (${row.toBankAccountId.slice(0, 8)}...)`;
};

const formatFromApexLabel = (row: AdminICTWithdrawalRequestRow): string => {
    const a = row.fromApexAccount;
    if (a == null) {
        return `Apex (${row.fromApexAccountId.slice(0, 8)}...)`;
    }
    if (a.nickname != null && a.nickname.trim() !== '') {
        return a.nickname.trim();
    }
    const last4 =
        a.accountNumber.length >= 4
            ? a.accountNumber.slice(-4)
            : a.accountNumber;
    const registrationType =
        a.registrationType as SupportedApexAccountRegistrationType;
    const accountTypeLabel =
        ReadableApexAccountRegistrationType[registrationType] ??
        'Investment account';
    return `${accountTypeLabel} · ${last4}`;
};

const formatRowDate = (iso: string | null | undefined): string =>
    formatOptional(iso, SHORT_READABLE_DATE_FORMAT, '');

const memberDisplayName = (row: AdminICTWithdrawalRequestRow): string =>
    getPersonDisplayName(
        row.user == null
            ? null
            : {
                  firstName: row.user.legalFirstName ?? row.user.firstName,
                  lastName: row.user.legalLastName ?? row.user.lastName,
                  email: row.user.email,
              },
        'Member',
    );

const invalidateIctWithdrawalRequestQueries = async (
    queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> => {
    await queryClient.invalidateQueries({
        queryKey: [ICT_WITHDRAWAL_REQUESTS_QUERY_KEY],
    });
};

const formatAmountCell = (row: AdminICTWithdrawalRequestRow): string =>
    row.amountCents == null ? 'N/A' : centsToFormattedDollars(row.amountCents);

const copyableIdRow = (id: string) => (
    <CopyableTextField value={id}>{id}</CopyableTextField>
);

const WithdrawalRequestsTable = ({
    title,
    rows,
    showActionColumn,
    showDeleteColumn = false,
    adminUserId,
    advancingId,
    deletingId,
    onAdvance,
    onDelete,
}: WithdrawalTableProps) => {
    const linkTextStyle = {
        color: '$dropletBlue1100',
        textDecorationLine: 'underline' as const,
        cursor: 'pointer' as const,
    };

    const membershipHref = (membershipId: string): Href =>
        `/membership/${membershipId}` as Href;

    return (
        <YStack gap={12} width="100%">
            <H6Emphasized>{title}</H6Emphasized>

            <DataTable
                value={rows}
                size="small"
                stripedRows
                showGridlines
                sortMode="single"
                removableSort
                tableStyle={{ width: '100%', fontSize: 13 }}
                scrollable
                scrollHeight="420px"
                dataKey="id"
                emptyMessage="No rows to show."
            >
                <Column
                    header="Member"
                    style={{ minWidth: 168 }}
                    body={(row: AdminICTWithdrawalRequestRow) => (
                        <YStack gap={4} maxWidth="100%">
                            <Link href={membershipHref(row.membershipId)}>
                                <BodySmallRegular style={linkTextStyle}>
                                    {memberDisplayName(row)}
                                </BodySmallRegular>
                            </Link>
                            {copyableIdRow(row.membershipId)}
                        </YStack>
                    )}
                />
                <Column
                    field="status"
                    header="Status"
                    sortable
                    alignHeader="center"
                    headerStyle={{ textAlign: 'center' }}
                    style={{ minWidth: 132 }}
                    bodyStyle={{ textAlign: 'center' }}
                    body={(row: AdminICTWithdrawalRequestRow) => (
                        <XStack justifyContent="center" width="100%">
                            <Tag
                                value={transformEnumToDisplay(row.status)}
                                severity={statusTagSeverity(row.status)}
                            />
                        </XStack>
                    )}
                />
                <Column
                    field="amountCents"
                    header="Amount"
                    sortable
                    style={{ minWidth: 96 }}
                    body={(row: AdminICTWithdrawalRequestRow) => (
                        <BodySmallRegular>
                            {formatAmountCell(row)}
                        </BodySmallRegular>
                    )}
                />
                <Column
                    field="isLiquidated"
                    header="Full disbursement"
                    sortable
                    alignHeader="center"
                    headerStyle={{ textAlign: 'center' }}
                    style={{ minWidth: 148 }}
                    bodyStyle={{ textAlign: 'center' }}
                    body={(row: AdminICTWithdrawalRequestRow) => (
                        <XStack justifyContent="center" width="100%">
                            <Tag
                                value={row.isLiquidated ? 'Yes' : 'No'}
                                severity={row.isLiquidated ? 'success' : 'info'}
                            />
                        </XStack>
                    )}
                />
                <Column
                    header="From (Apex)"
                    style={{ minWidth: 200 }}
                    body={(row: AdminICTWithdrawalRequestRow) => (
                        <YStack gap={4} maxWidth="100%">
                            <Link href={membershipHref(row.membershipId)}>
                                <BodySmallRegular style={linkTextStyle}>
                                    {formatFromApexLabel(row)}
                                </BodySmallRegular>
                            </Link>
                            {copyableIdRow(row.fromApexAccountId)}
                        </YStack>
                    )}
                />
                <Column
                    header="To (bank)"
                    style={{ minWidth: 200 }}
                    body={(row: AdminICTWithdrawalRequestRow) => (
                        <YStack gap={4} maxWidth="100%">
                            <Link href={membershipHref(row.membershipId)}>
                                <BodySmallRegular style={linkTextStyle}>
                                    {formatToBankLabel(row)}
                                </BodySmallRegular>
                            </Link>
                            {copyableIdRow(row.toBankAccountId)}
                        </YStack>
                    )}
                />
                <Column
                    field="distributionReason"
                    header="Reason"
                    sortable
                    style={{ minWidth: 128 }}
                    bodyStyle={{ wordBreak: 'break-word' }}
                    body={(row: AdminICTWithdrawalRequestRow) => {
                        const reason = row.distributionReason?.trim();
                        return reason != null && reason !== ''
                            ? transformEnumToDisplay(reason)
                            : '';
                    }}
                />
                <Column
                    field="createdAt"
                    header="Requested"
                    sortable
                    style={{ minWidth: 118 }}
                    body={(row: AdminICTWithdrawalRequestRow) =>
                        formatRowDate(row.createdAt)
                    }
                />
                {showActionColumn && (
                    <Column
                        header="Action"
                        alignHeader="center"
                        headerStyle={{ textAlign: 'center' }}
                        style={{ minWidth: 148 }}
                        bodyStyle={{ textAlign: 'center' }}
                        body={(row: AdminICTWithdrawalRequestRow) => {
                            const next = getNextPipelineStatus(row.status);
                            if (next == null) {
                                return null;
                            }
                            const busy =
                                advancingId != null && advancingId === row.id;
                            const isSelfApproval =
                                next === 'TRANSFER_APPROVED' &&
                                adminUserId != null &&
                                adminUserId !== '' &&
                                row.completedBy === adminUserId;
                            const nextLabel = transformEnumToDisplay(next);
                            return (
                                <XStack
                                    justifyContent="center"
                                    width="100%"
                                    onStartShouldSetResponder={() => true}
                                >
                                    <Button
                                        text={busy ? '' : nextLabel}
                                        icon={
                                            busy ? (
                                                <Spinner
                                                    size={SpinnerSize.SMALL}
                                                    width={SpinnerSize.SMALL}
                                                    maxWidth={SpinnerSize.SMALL}
                                                />
                                            ) : undefined
                                        }
                                        variant={ButtonVariant.SECONDARY}
                                        size={ButtonSize.TINY}
                                        width="auto"
                                        disabled={busy || isSelfApproval}
                                        onPress={(e) => {
                                            e.stopPropagation?.();
                                            void onAdvance?.(row, next);
                                        }}
                                    />
                                </XStack>
                            );
                        }}
                    />
                )}
                {showDeleteColumn && (
                    <Column
                        header=""
                        alignHeader="center"
                        style={{ width: '3rem', minWidth: '3rem' }}
                        bodyStyle={{ textAlign: 'center' }}
                        body={(row: AdminICTWithdrawalRequestRow) => {
                            const busy =
                                deletingId != null && deletingId === row.id;
                            return (
                                <XStack
                                    justifyContent="center"
                                    width="100%"
                                    onStartShouldSetResponder={() => true}
                                >
                                    <IconButton
                                        variant="tertiary"
                                        tone="danger"
                                        size="tiny"
                                        icon={TrashIcon}
                                        disabled={busy}
                                        aria-label="Delete withdrawal request"
                                        onPress={(e) => {
                                            e.stopPropagation?.();
                                            confirmDialog({
                                                header: 'Delete withdrawal request?',
                                                message: `Are you sure you want to delete the withdrawal request for ${memberDisplayName(row)}? This action cannot be undone.`,
                                                acceptLabel: 'Delete',
                                                rejectLabel: 'Cancel',
                                                accept: () => {
                                                    void onDelete?.(row);
                                                },
                                                visible: true,
                                            });
                                        }}
                                    />
                                </XStack>
                            );
                        }}
                    />
                )}
            </DataTable>
        </YStack>
    );
};

export const ImOpsManualIctWithdrawalsPanel = () => {
    const adminUser = useAtomValue(adminUserAtom);
    const toast = useToastController();
    const queryClient = useQueryClient();
    const [advancingId, setAdvancingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const { data: rows, isLoading } = useGetICTWithdrawalRequests({});

    const { mutateAsync: patchRequest } = usePatchICTWithdrawalRequest({
        onSuccess: async () => {
            await invalidateIctWithdrawalRequestQueries(queryClient);
        },
    });

    const { mutateAsync: deleteRequest } = useDeleteICTWithdrawalRequest({
        onSuccess: async () => {
            await invalidateIctWithdrawalRequestQueries(queryClient);
        },
    });

    const { openRows, doneRows } = useMemo(() => {
        const list = rows ?? [];
        const open: AdminICTWithdrawalRequestRow[] = [];
        const done: AdminICTWithdrawalRequestRow[] = [];
        for (const r of list) {
            if (r.status === 'DONE') {
                done.push(r);
            } else {
                open.push(r);
            }
        }
        return { openRows: open, doneRows: done };
    }, [rows]);

    const handleAdvance = async (
        row: AdminICTWithdrawalRequestRow,
        nextStatus: ICTWithdrawalRequestStatus,
    ) => {
        setAdvancingId(row.id);
        try {
            await patchRequest({
                urlParams: { id: row.id },
                body: { status: nextStatus },
            });
            toast.show('Status updated', MutationSuccessToastOptions);
        } catch (err) {
            toast.show(
                err instanceof Error ? err.message : String(err),
                MutationErrorToastOptions(),
            );
        } finally {
            setAdvancingId(null);
        }
    };

    const handleDelete = async (row: AdminICTWithdrawalRequestRow) => {
        setDeletingId(row.id);
        try {
            await deleteRequest({
                urlParams: { id: row.id },
            });
            toast.show(
                'Withdrawal request deleted',
                MutationSuccessToastOptions,
            );
        } catch (err) {
            toast.show(
                err instanceof Error ? err.message : String(err),
                MutationErrorToastOptions(),
            );
        } finally {
            setDeletingId(null);
        }
    };

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <YStack gap={40} width="100%">
            <H6Emphasized>Manual ICT withdrawal requests</H6Emphasized>

            <WithdrawalRequestsTable
                title={`Open (${openRows.length})`}
                rows={openRows}
                showActionColumn
                showDeleteColumn
                adminUserId={adminUser.id}
                advancingId={advancingId}
                deletingId={deletingId}
                onAdvance={handleAdvance}
                onDelete={handleDelete}
            />

            <WithdrawalRequestsTable
                title={`Completed (${doneRows.length})`}
                rows={doneRows}
                showActionColumn={false}
            />
        </YStack>
    );
};
